// ModelScene.jsx
import React, { useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { TrackballControls, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
// The GLB and the assembly JSON are fetched outside the axios instance, so they attach the
// Bearer token themselves.
import { authHeader } from '../../utilities/globalServiceUtils';

// --------- AXIS / ORIENTATION CONFIG ---------
const AXIS_VECTORS = {
    X: new THREE.Vector3(1, 0, 0),
    Y: new THREE.Vector3(0, 1, 0),
    Z: new THREE.Vector3(0, 0, 1),
};

const normalizeAxisKey = (axis, fallback = 'Y') => {
    const raw = String(axis || '')
        .toUpperCase()
        .replace(/[^A-Z]/g, '');
    if (raw === 'X' || raw === 'X_UP') return 'X';
    if (raw === 'Y' || raw === 'Y_UP') return 'Y';
    if (raw === 'Z' || raw === 'Z_UP') return 'Z';
    return fallback;
};

const getNormalizedOrientation = (orientation, fallbackUpAxis) => {
    const up = normalizeAxisKey(orientation?.up || fallbackUpAxis, 'Y');
    const left = normalizeAxisKey(orientation?.left, up === 'X' ? 'Z' : 'X');
    const right = normalizeAxisKey(orientation?.right, 'X');
    const isValid = new Set([up, left, right]).size === 3;

    if (isValid) return { up, left, right };

    const fallbackLeft = up === 'X' ? 'Z' : 'X';
    const fallbackRight =
        ['X', 'Y', 'Z'].find((axis) => axis !== up && axis !== fallbackLeft) || 'Y';
    return { up, left: fallbackLeft, right: fallbackRight };
};

const getOrientationQuaternion = (orientation) => {
    const sourceUp = AXIS_VECTORS[orientation.up].clone().normalize();
    const rightAxis = AXIS_VECTORS[orientation.right].clone().normalize();
    let sourceRight = rightAxis
        .clone()
        .addScaledVector(sourceUp, -rightAxis.dot(sourceUp))
        .normalize();
    if (sourceRight.lengthSq() === 0) sourceRight = rightAxis.clone();

    const sourceForward = sourceRight.clone().cross(sourceUp).normalize();

    if (sourceForward.lengthSq() === 0) {
        return new THREE.Quaternion();
    }

    const sourceBasis = new THREE.Matrix4().makeBasis(
        sourceRight,
        sourceUp,
        sourceForward
    );
    const rotationMatrix = sourceBasis.clone().invert();
    const quat = new THREE.Quaternion();
    quat.setFromRotationMatrix(rotationMatrix);
    return quat;
};

const getLegacyUpAxisQuaternion = (axis) => {
    const source = AXIS_VECTORS[normalizeAxisKey(axis, 'Y')] || AXIS_VECTORS.Y;
    const target = AXIS_VECTORS.Y;
    const quat = new THREE.Quaternion();
    quat.setFromUnitVectors(source, target);
    return quat;
};

const applyModelUpAxis = (root, axis, orientation) => {
    if (!root) return;
    const hasExplicitOrientation = !!(
        orientation &&
        orientation.up &&
        orientation.left &&
        orientation.right
    );

    if (!root.userData.__baseQuaternion) {
        root.userData.__baseQuaternion = root.quaternion.clone();
    }

    const baseQuat = root.userData.__baseQuaternion;
    const orientationQuat = hasExplicitOrientation
        ? getOrientationQuaternion(getNormalizedOrientation(orientation, axis))
        : getLegacyUpAxisQuaternion(axis);

    root.quaternion.copy(baseQuat).multiply(orientationQuat);
    root.updateMatrixWorld(true);
};

const getCameraUpVectorForAxis = () => {
    // Keep camera/world up fixed so axis settings rotate the model itself.
    return AXIS_VECTORS.Y.clone();
};

const CAMERA_CORNER_CHOICES = new Set([
    'isometric',
    'front',
    'back',
    'left',
    'right',
    'top',
    'bottom',
]);

const normalizeCameraCorner = (value) => {
    const raw = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, '-')
        .replace(/[^a-z-]/g, '');
    return CAMERA_CORNER_CHOICES.has(raw) ? raw : 'isometric';
};

const getPreferredIsometricDirection = (sceneQuaternion, corner = 'front-right') => {
    const normalizedCorner = normalizeCameraCorner(corner);
    const up = AXIS_VECTORS.Y.clone();
    const xAxis = AXIS_VECTORS.X.clone().applyQuaternion(sceneQuaternion || new THREE.Quaternion()).normalize();
    const zAxis = AXIS_VECTORS.Z.clone().applyQuaternion(sceneQuaternion || new THREE.Quaternion()).normalize();
    const xH = xAxis.clone().addScaledVector(up, -xAxis.dot(up)).normalize();
    const zH = zAxis.clone().addScaledVector(up, -zAxis.dot(up)).normalize();

    if (xH.lengthSq() === 0 || zH.lengthSq() === 0) {
        return { dir: new THREE.Vector3(0.75, 0.9, 0.75).normalize(), distanceMultiplier: 1 };
    }

    const compose = (upWeight, xWeight, zWeight) =>
        up.clone()
            .multiplyScalar(upWeight)
            .add(xH.clone().multiplyScalar(xWeight))
            .add(zH.clone().multiplyScalar(zWeight))
            .normalize();

    switch (normalizedCorner) {
        case 'isometric':
            // Match legacy viewer default "isometric" side (non-mirrored opening).
            return { dir: compose(1.0, -0.75, 0.75), distanceMultiplier: 1 };
        case 'front':
            return { dir: compose(0.35, 0, 1), distanceMultiplier: 1 };
        case 'back':
            return { dir: compose(0.35, 0, -1), distanceMultiplier: 1 };
        case 'left':
            return { dir: compose(0.35, -1, 0), distanceMultiplier: 1 };
        case 'right':
            return { dir: compose(0.35, 1, 0), distanceMultiplier: 1 };
        case 'top':
            return { dir: up.clone().normalize(), distanceMultiplier: 1 };
        case 'bottom':
            return { dir: up.clone().multiplyScalar(-1).normalize(), distanceMultiplier: 1 };
        default:
            return { dir: compose(0.9, 0.75, 0.75), distanceMultiplier: 1 };
    }
};



// Attach Three.js objects to each JSON node, using both:
// - node.name      (PRODUCT name, e.g. "75944-08")
// - node.naouIds[] (instance IDs, e.g. "NAUO146")
function attachObjectsToJson(node, nameToObjects) {
    if (!node) return;

    const keys = new Set();

    if (node.name) {
        keys.add(node.name);
    }

    if (Array.isArray(node.naouIds)) {
        node.naouIds.forEach((id) => {
            if (id) keys.add(id);
        });
    }

    const objects = [];
    for (const k of keys) {
        const arr = nameToObjects.get(k);
        if (arr) {
            objects.push(...arr);
        }
    }

    node.objects = objects;

    if (Array.isArray(node.children)) {
        node.children.forEach((child) =>
            attachObjectsToJson(child, nameToObjects)
        );
    }
}

function findNodeByObject(node, targetUuid) {
    if (!node) return null;

    if (Array.isArray(node.objects)) {
        if (node.objects.some((obj) => obj.uuid === targetUuid)) {
            return node;
        }
    }

    if (Array.isArray(node.children)) {
        for (const child of node.children) {
            const found = findNodeByObject(child, targetUuid);
            if (found) return found;
        }
    }

    return null;
}

function findNodeByProductRef(node, productRef, result = []) {
    if (!node) return result;

    const nodeRef = node.productRef;
    const normalizedNodeRef = String(nodeRef ?? '').trim().replace(/^#+/, '').toLowerCase();
    const normalizedProductRef = String(productRef ?? '').trim().replace(/^#+/, '').toLowerCase();
    if (
        nodeRef !== null &&
        nodeRef !== undefined &&
        normalizedNodeRef === normalizedProductRef
    ) {
        result.push(node);
    }

    if (Array.isArray(node.children)) {
        node.children.forEach((child) =>
            findNodeByProductRef(child, productRef, result)
        );
    }

    return result;
}

function collectNodeRootObjects(node, targetSet = new Set()) {
    if (!node || !targetSet) return targetSet;

    if (Array.isArray(node.objects) && node.objects.length > 0) {
        node.objects.forEach((obj) => {
            if (obj) {
                targetSet.add(obj);
            }
        });
    }

    if (Array.isArray(node.children)) {
        node.children.forEach((child) =>
            collectNodeRootObjects(child, targetSet)
        );
    }

    return targetSet;
}

function collectAncestorObjects(roots, targetSet = new Set()) {
    if (!Array.isArray(roots) || !roots.length) return targetSet;

    roots.forEach((root) => {
        if (!root || !root.parent) return;
        let current = root.parent;
        while (current) {
            targetSet.add(current);
            current = current.parent;
        }
    });

    return targetSet;
}

// Build a parent map for assembly nodes so we can walk up the tree
function buildAssemblyParentMap(node, parent = null, map = new Map()) {
    if (!node) return map;
    map.set(node, parent);
    if (Array.isArray(node.children)) {
        node.children.forEach((child) =>
            buildAssemblyParentMap(child, node, map)
        );
    }
    return map;
}

function resolveAssemblyNodeFromResolverResult(result, fallback = null) {
    if (!result) return fallback;
    if (result.targetNode) return result.targetNode;
    return result;
}

const ModelScene = React.forwardRef(function ModelScene(
    {
        glbPath,
        assemblyPath,
        onPartSelected,
        onPartDoubleClick,
        onAssemblyMapped,
        lightSettings,
        onSelectionChange, // notifies parent about current logical selection
        onVisibilityChange, // notifies parent about productRef -> visible map
        resolveSelectionNode,
        explodeFactor = 0, // 0..1
        onModelLoaded, // notify when GLB  assembly are ready
        navMode = 'rotate', // 'rotate' | 'pan'
        highlightColor = '#ffcc00',
        highlightOpacity = 1,
        modelAxis = 'Y_UP',
        modelOrientation = null,
        defaultCameraCorner = 'isometric',
    },
    ref
) {
    const { camera, gl, raycaster, size } = useThree();
    const controlsRef = useRef();
    const orientationUp = modelOrientation?.up;
    const orientationLeft = modelOrientation?.left;
    const orientationRight = modelOrientation?.right;
    const explicitOrientation = useMemo(() => {
        if (!orientationUp || !orientationLeft || !orientationRight) return null;
        return getNormalizedOrientation(
            { up: orientationUp, left: orientationLeft, right: orientationRight },
            modelAxis
        );
    }, [modelAxis, orientationUp, orientationLeft, orientationRight]);

    const lastSelectionRef = useRef([]); // [Mesh] currently highlighted
    const lastRootsRef = useRef([]); // [Object3D] roots for current selection (subtrees)
    const assemblyTreeRef = useRef(null); // JSON tree
    const assemblyParentMapRef = useRef(new Map()); // node -> parent node
    const allMeshesRef = useRef(new Set());
    const uuidToMeshRef = useRef(new Map()); // uuid -> Mesh

    const selectedPartsMetaRef = useRef([]); // logical parts selected (per assembly node)
    const selectionKeySetRef = useRef(new Set());

    const defaultCameraPosRef = useRef(null);
    const defaultTargetRef = useRef(new THREE.Vector3(0, 0, 0));
    const cameraTransitionRef = useRef(null);

    // FILO stack of hidden groups; each entry = array<Object3D> (subtrees)
    const hiddenStackRef = useRef([]);

    // Explosion data
    const modelRadiusRef = useRef(2);
    const explodeDataRef = useRef({
        entries: [], // { root, basePosition, direction }
        center: new THREE.Vector3(),
        radius: 2,
    });

    // Toggle state for zoom-to-selected
    const zoomToggleStateRef = useRef({
        isZoomed: false,
        prevPos: null,
        prevTarget: null,
    });
    const lastSelectionPointRef = useRef(null);

    useFrame((_, delta) => {
        const transition = cameraTransitionRef.current;
        const controls = controlsRef.current;
        if (!transition || !controls) return;

        transition.elapsed = Math.min(
            transition.duration,
            transition.elapsed + Math.max(delta, 0)
        );
        const progress = transition.duration > 0
            ? transition.elapsed / transition.duration
            : 1;
        // Ease-out cubic: quick start, gentle arrival at the selected part.
        const eased = 1 - Math.pow(1 - progress, 3);

        camera.position.lerpVectors(
            transition.startPosition,
            transition.endPosition,
            eased
        );
        controls.target.lerpVectors(
            transition.startTarget,
            transition.endTarget,
            eased
        );
        camera.updateProjectionMatrix();
        controls.update();

        if (progress >= 1) {
            cameraTransitionRef.current = null;
        }
    });

    // State for "Show Parent" stepping
    const showParentStateRef = useRef(null);
    // { baseNodes: [node...], chains: [[node, parent, ...root], ...], step: number }
    const globalTransparencyStateRef = useRef({
        enabled: false,
        opacity: 0.15,
        exceptKey: '',
    });
    const lastPointerSelectRef = useRef({ selectionKey: null, ts: 0 });
    const suppressNativeDoubleClickRef = useRef(false);
    const shiftPanActiveRef = useRef(false);

    // useDraco/useMeshopt are passed explicitly only to reach the 4th argument. Unlike some other
    // deployments, glbPath here is always a real network URL that GLTFLoader fetches itself
    // (ModelViewer passes the /stock-models URL straight through, with no streaming-to-blob step),
    // so this header is the ONLY thing authenticating the model download — not a fallback.
    const { scene } = useGLTF(glbPath, true, true, (loader) => {
        const headers = authHeader();
        if (headers.Authorization) loader.setRequestHeader(headers);
    });

    // ---------- Visibility helper (tool + tree) ----------
    const applyVisibilityFromFlags = (obj) => {
        const hiddenByTool = !!obj.userData.__hiddenByTool;
        const hiddenByTree = !!obj.userData.__hiddenByTree;
        const hiddenByView = !!obj.userData.__hiddenByView;
        const shouldBeVisible = !(hiddenByTool || hiddenByTree || hiddenByView);

        obj.visible = shouldBeVisible;
        if (shouldBeVisible) {
            obj.layers.enable(0);
        } else {
            obj.layers.disable(0);
        }
    };

    const computeVisibilityByProductRef = () => {
        const root = assemblyTreeRef.current;
        if (!root) return {};

        const visibilityMap = {};

        const visit = (node) => {
            if (!node) return;

            if (
                node.productRef !== null &&
                node.productRef !== undefined &&
                Array.isArray(node.objects) &&
                node.objects.length > 0
            ) {
                const key = String(node.productRef);
                let visible = false;

                for (const rootObj of node.objects) {
                    if (!rootObj) continue;
                    rootObj.traverse((obj) => {
                        if (!visible && obj.isObject3D && obj.visible) {
                            visible = true;
                        }
                    });
                    if (visible) break;
                }

                visibilityMap[key] = visible;
            }

            if (Array.isArray(node.children)) {
                node.children.forEach(visit);
            }
        };

        visit(root);
        return visibilityMap;
    };

    const notifyVisibilityChange = () => {
        if (!onVisibilityChange) return;
        const vis = computeVisibilityByProductRef();
        onVisibilityChange(vis);
    };

    // ---------- Highlight helpers ----------
    const restoreMeshOriginalMaterial = (mesh) => {
        if (!mesh || !mesh.userData) return;
        const selState = mesh.userData.__selectionState;
        if (!selState) return;

        const { originalMaterial, highlightMaterial, originalRenderOrder } = selState;

        // Dispose highlight materials
        if (Array.isArray(highlightMaterial)) {
            highlightMaterial.forEach(
                (mat) => mat && mat.dispose && mat.dispose()
            );
        } else if (highlightMaterial && highlightMaterial.dispose) {
            highlightMaterial.dispose();
        }

        // Restore original materials
        mesh.material = originalMaterial;
        if (originalRenderOrder !== undefined) {
            mesh.renderOrder = originalRenderOrder;
        }
        delete mesh.userData.__selectionState;
    };

    const clearLastSelection = () => {
        const meshes = lastSelectionRef.current;
        if (!meshes || meshes.length === 0) return;

        meshes.forEach((mesh) => {
            restoreMeshOriginalMaterial(mesh);
        });

        lastSelectionRef.current = [];
    };

    const highlightMeshesReplace = (meshes) => {
        const highlighted = [];

        meshes.forEach((mesh) => {
            if (!(mesh instanceof THREE.Mesh) || !mesh.material) return;

            // Avoid double-highlighting the same mesh
            if (mesh.userData.__selectionState) {
                highlighted.push(mesh);
                return;
            }

            const applyHighlightMaterial = (mat) => {
                const clone = mat.clone();
                if (clone.color) {
                    clone.color = new THREE.Color(highlightColor);
                }
                if (typeof highlightOpacity === "number" && highlightOpacity < 1) {
                    clone.transparent = true;
                    clone.opacity = highlightOpacity;
                } else {
                    clone.transparent = false;
                    clone.opacity = 1;
                }
                // Render the selected part above surrounding machine geometry
                // so deeply nested parts remain identifiable without hiding
                // the rest of the model.
                clone.depthTest = false;
                clone.depthWrite = false;
                clone.needsUpdate = true;
                return clone;
            };

            const originalRenderOrder = mesh.renderOrder;
            mesh.renderOrder = 1000;

            if (Array.isArray(mesh.material)) {
                const originalMaterials = mesh.material;
                const highlightMaterials = originalMaterials.map(applyHighlightMaterial);
                mesh.material = highlightMaterials;
                mesh.userData.__selectionState = {
                    originalMaterial: originalMaterials,
                    highlightMaterial: highlightMaterials,
                    originalRenderOrder,
                };
            } else {
                const originalMaterial = mesh.material;
                const highlightMaterial = applyHighlightMaterial(originalMaterial);
                mesh.material = highlightMaterial;
                mesh.userData.__selectionState = {
                    originalMaterial,
                    highlightMaterial,
                    originalRenderOrder,
                };
            }

            highlighted.push(mesh);
        });

        lastSelectionRef.current = highlighted;
    };

    // Add highlight without clearing existing selection
    const highlightMeshesAdd = (meshes) => {
        const selectionSet = new Set(lastSelectionRef.current || []);

        meshes.forEach((mesh) => {
            if (!(mesh instanceof THREE.Mesh) || !mesh.material) return;

            // Already highlighted -> just ensure it's tracked
            if (mesh.userData.__selectionState) {
                selectionSet.add(mesh);
                return;
            }

            const applyHighlightMaterial = (mat) => {
                const clone = mat.clone();
                if (clone.color) {
                    clone.color = new THREE.Color(highlightColor);
                }
                if (typeof highlightOpacity === "number" && highlightOpacity < 1) {
                    clone.transparent = true;
                    clone.opacity = highlightOpacity;
                } else {
                    clone.transparent = false;
                    clone.opacity = 1;
                }
                clone.needsUpdate = true;
                return clone;
            };

            if (Array.isArray(mesh.material)) {
                const originalMaterials = mesh.material;
                const highlightMaterials = originalMaterials.map(applyHighlightMaterial);
                mesh.material = highlightMaterials;
                mesh.userData.__selectionState = {
                    originalMaterial: originalMaterials,
                    highlightMaterial: highlightMaterials,
                };
            } else {
                const originalMaterial = mesh.material;
                const highlightMaterial = applyHighlightMaterial(originalMaterial);
                mesh.material = highlightMaterial;
                mesh.userData.__selectionState = {
                    originalMaterial,
                    highlightMaterial,
                };
            }

            selectionSet.add(mesh);
        });

        lastSelectionRef.current = Array.from(selectionSet);
    };

    const resetSelectionTracking = (
        {
            notifySelectionChange = true,
            notifyPartDeselected = true,
        } = {}
    ) => {
        clearLastSelection();
        lastRootsRef.current = [];
        selectedPartsMetaRef.current = [];
        selectionKeySetRef.current = new Set();

        if (notifySelectionChange && onSelectionChange) {
            onSelectionChange([]);
        }
        if (notifyPartDeselected && onPartSelected) {
            onPartSelected(null);
        }

        zoomToggleStateRef.current = {
            isZoomed: false,
            prevPos: null,
            prevTarget: null,
        };
        lastSelectionPointRef.current = null;

        showParentStateRef.current = null;
    };

    // ---------- Camera helpers ----------
    const focusOnRoots = (roots) => {
        const controls = controlsRef.current;
        if (!controls) return;
        if (!roots || !roots.length) return;

        const box = new THREE.Box3();
        roots.forEach((root) => {
            if (root) {
                box.expandByObject(root);
            }
        });

        if (box.isEmpty()) return;

        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        box.getCenter(center);
        box.getSize(size);
        // Always aim at the selected bounds center so the part appears in the
        // middle of the canvas rather than at the clicked surface point.
        const targetPoint = center.clone();

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = (camera.fov * Math.PI) / 180;
        let dist = Math.abs(maxDim / 2 / Math.tan(fov / 2));
        dist *= 1.5; // padding

        const dir = new THREE.Vector3().subVectors(
            camera.position,
            controls.target
        );
        if (dir.lengthSq() === 0) {
            dir.set(1, 1, 1).normalize();
        } else {
            dir.normalize();
        }

        const newPos = targetPoint.clone().add(dir.multiplyScalar(dist));

        cameraTransitionRef.current = {
            startPosition: camera.position.clone(),
            endPosition: newPos,
            startTarget: controls.target.clone(),
            endTarget: targetPoint,
            elapsed: 0,
            duration: 0.65,
        };
    };

    // ---------- Scene state helpers ----------
    const resetSceneState = () => {
        // Ensure any temporary global transparency materials are removed first.
        setGlobalTransparency(false);

        // 1) Unhide everything from the hidden stack
        hiddenStackRef.current.forEach((group) => {
            group.forEach((obj) => {
                if (!obj) return;
                obj.userData.__hiddenByTool = false;
                obj.userData.__hiddenByTree = false;
                obj.userData.__hiddenByView = false;
                applyVisibilityFromFlags(obj);
            });
        });
        hiddenStackRef.current = [];

        // 2) Clear selection/metadata
        resetSelectionTracking();

        // 3) Reset all objects (groups + meshes)
        scene.traverse((obj) => {
            if (!obj.isObject3D || obj === scene) return;

            obj.userData.__hiddenByTool = false;
            obj.userData.__hiddenByTree = false;
            obj.userData.__hiddenByView = false;
            applyVisibilityFromFlags(obj);

            if (obj instanceof THREE.Mesh) {
                const applySolid = (mat) => {
                    if (!mat) return;
                    if ('opacity' in mat) {
                        mat.opacity = 1;
                        mat.transparent = false;
                        mat.depthWrite = true;
                        mat.needsUpdate = true;
                    }
                };

                if (Array.isArray(obj.material)) {
                    obj.material.forEach(applySolid);
                } else {
                    applySolid(obj.material);
                }
            }
        });

        notifyVisibilityChange();
    };

    // Hide = traverse selection roots, mark them hidden by tool, and recompute visibility
    const hideSelected = () => {
        const roots = lastRootsRef.current;
        if (!roots || !roots.length) return;

        const hiddenObjects = [];

        roots.forEach((root) => {
            if (!root) return;

            root.traverse((obj) => {
                if (!obj.isObject3D) return;

                if (obj.userData.__hiddenByTool) return;

                obj.userData.__hiddenByTool = true;
                applyVisibilityFromFlags(obj);
                hiddenObjects.push(obj);
            });
        });

        if (hiddenObjects.length > 0) {
            hiddenStackRef.current.push(hiddenObjects); // FILO
        }

        // Clear highlight (we hid it), keep logical selection
        clearLastSelection();
        notifyVisibilityChange();
    };

    const unhideLastHiddenGroup = () => {
        const stack = hiddenStackRef.current;
        if (!stack.length) return false;

        const hiddenObjects = stack.pop();
        hiddenObjects.forEach((obj) => {
            if (!obj) return;
            obj.userData.__hiddenByTool = false;
            applyVisibilityFromFlags(obj);
        });

        notifyVisibilityChange();
        return true;
    };

    const isUnderAnyRoot = (obj, rootsSet) => {
        let current = obj;
        while (current) {
            if (rootsSet.has(current)) return true;
            current = current.parent;
        }
        return false;
    };

    const isolateSelected = (options = {}) => {
        const roots = lastRootsRef.current;
        if (!roots || !roots.length) return;

        const rootsSet = new Set(roots);
        const ancestorSet = collectAncestorObjects(roots, new Set());

        // First mark all objects as hiddenByTool
        scene.traverse((obj) => {
            if (obj !== scene && obj.isObject3D) {
                const keepVisible =
                    isUnderAnyRoot(obj, rootsSet) || ancestorSet.has(obj);
                obj.userData.__hiddenByView = !keepVisible;
            }
        });

        // Apply visibility flags
        scene.traverse((obj) => {
            if (obj !== scene && obj.isObject3D) {
                applyVisibilityFromFlags(obj);
            }
        });

        notifyVisibilityChange();

        const { focus = true } = options || {};
        if (focus) {
            // Zoom to the isolated selection
            focusOnRoots(roots);
        }
    };

    // Show ONLY the parts whose productRefs are in productRefs.
    // If productRefs is empty or falsy, default is to show everything again.
    // options.emptyMode: "showAll" (default) or "hideAll"
    const showOnlyProductRefs = (productRefs, options = {}) => {
        const rootAssembly = assemblyTreeRef.current;
        if (!scene) return;

        const { emptyMode = "showAll" } = options || {};

        // If no refs given -> clear __hiddenByTool and show everything
        if (
            !Array.isArray(productRefs) ||
            productRefs.length === 0
        ) {
            scene.traverse((obj) => {
                if (obj !== scene && obj.isObject3D) {
                    obj.userData.__hiddenByView = emptyMode === "hideAll";
                    applyVisibilityFromFlags(obj);
                }
            });
            notifyVisibilityChange();
            return;
        }

        if (!rootAssembly) return;

        const refSet = new Set(productRefs.map((r) => String(r)));
        const roots = [];

        // Collect root 3D objects for all matching productRefs
        refSet.forEach((ref) => {
            const nodes = findNodeByProductRef(rootAssembly, ref);
            if (!nodes || !nodes.length) return;

            nodes.forEach((node) => {
                if (!Array.isArray(node.objects)) return;
                node.objects.forEach((rootObj) => {
                    if (rootObj) roots.push(rootObj);
                });
            });
        });

        if (!roots.length) {
            scene.traverse((obj) => {
                if (obj !== scene && obj.isObject3D) {
                    obj.userData.__hiddenByView = emptyMode === "hideAll";
                    applyVisibilityFromFlags(obj);
                }
            });
            notifyVisibilityChange();
            return;
        }

        const rootsSet = new Set(roots);
        const ancestorSet = collectAncestorObjects(roots, new Set());

        // Mark all non-matching objects as hiddenByTool
        scene.traverse((obj) => {
            if (obj !== scene && obj.isObject3D) {
                const keepVisible =
                    isUnderAnyRoot(obj, rootsSet) || ancestorSet.has(obj);
                obj.userData.__hiddenByView = !keepVisible;
                applyVisibilityFromFlags(obj);
            }
        });

        notifyVisibilityChange();

        const { focus = true } = options;
        if (focus) {
            focusOnRoots(roots);
        }
    };


    const cloneSelectionOriginalMaterial = (mesh) => {
        const selState = mesh?.userData?.__selectionState;
        if (!selState || !selState.originalMaterial) return null;

        const previousOriginal = selState.originalMaterial;
        const previousMats = Array.isArray(previousOriginal)
            ? previousOriginal
            : [previousOriginal];
        const previousOwned = previousMats.every(
            (mat) => mat?.userData?.__selectionOwnedMaterial
        );

        const cloned = Array.isArray(previousOriginal)
            ? previousOriginal.map((mat) => (mat?.clone ? mat.clone() : mat))
            : (previousOriginal?.clone ? previousOriginal.clone() : previousOriginal);

        const clonedMats = Array.isArray(cloned) ? cloned : [cloned];
        clonedMats.forEach((mat) => {
            if (!mat) return;
            if (!mat.userData) mat.userData = {};
            mat.userData.__selectionOwnedMaterial = true;
        });

        if (mesh.userData.__globalTransparentOriginalMaterial === previousOriginal) {
            mesh.userData.__globalTransparentOriginalMaterial = cloned;
        }

        selState.originalMaterial = cloned;

        if (previousOwned && previousOriginal !== cloned) {
            const matsToDispose = Array.isArray(previousOriginal)
                ? previousOriginal
                : [previousOriginal];
            const disposeSet = new Set(matsToDispose.filter(Boolean));
            disposeSet.forEach((mat) => {
                if (mat?.dispose) mat.dispose();
            });
        }

        return cloned;
    };

    const applySolidMaterial = (mat) => {
        if (!mat) return;
        if ('opacity' in mat) {
            mat.opacity = 1;
            mat.transparent = false;
            mat.depthWrite = true;
            mat.needsUpdate = true;
        }
    };

    const applyTransparentMaterial = (mat, opacity = 0.2) => {
        if (!mat) return;
        if ('opacity' in mat) {
            mat.transparent = true;
            mat.opacity = opacity;
            mat.depthWrite = false;
            mat.needsUpdate = true;
        }
    };

    const applyMaterialModeToMesh = (mesh, transparent, options = {}) => {
        if (!mesh) return;
        const { opacity = 0.2, persistSelection = true } = options;
        const apply = transparent
            ? (mat) => applyTransparentMaterial(mat, opacity)
            : applySolidMaterial;

        if (Array.isArray(mesh.material)) {
            mesh.material.forEach(apply);
        } else {
            apply(mesh.material);
        }

        if (!persistSelection) return;

        const original = cloneSelectionOriginalMaterial(mesh);
        if (Array.isArray(original)) {
            original.forEach(apply);
        } else {
            apply(original);
        }
    };

    const collectMeshesForSelectionKeys = (selectionKeysInput = []) => {
        const rootAssembly = assemblyTreeRef.current;
        const meshes = new Set();
        const selectionKeys = Array.isArray(selectionKeysInput)
            ? selectionKeysInput
            : [selectionKeysInput];

        selectionKeys
            .map((key) => String(key || '').trim())
            .filter(Boolean)
            .forEach((key) => {
                if (key.startsWith('p:') && rootAssembly) {
                    const productRef = key.slice(2);
                    const nodes = findNodeByProductRef(rootAssembly, productRef);
                    nodes.forEach((node) => {
                        if (!Array.isArray(node.objects)) return;
                        node.objects.forEach((rootObj) => {
                            if (!rootObj) return;
                            rootObj.traverse((child) => {
                                if (child instanceof THREE.Mesh) {
                                    meshes.add(child);
                                }
                            });
                        });
                    });
                    return;
                }

                if (key.startsWith('u:')) {
                    const uuid = key.slice(2);
                    const obj = uuidToMeshRef.current.get(uuid);
                    if (!obj) return;
                    if (obj instanceof THREE.Mesh) {
                        meshes.add(obj);
                        return;
                    }
                    obj.traverse?.((child) => {
                        if (child instanceof THREE.Mesh) {
                            meshes.add(child);
                        }
                    });
                }
            });

        return Array.from(meshes);
    };

    const setSelectionKeysTransparent = (selectionKeys, options = {}) => {
        collectMeshesForSelectionKeys(selectionKeys).forEach((mesh) => {
            applyMaterialModeToMesh(mesh, true, options);
        });
    };

    const setSelectionKeysSolid = (selectionKeys, options = {}) => {
        collectMeshesForSelectionKeys(selectionKeys).forEach((mesh) => {
            applyMaterialModeToMesh(mesh, false, options);
        });
    };

    const makeSelectedSolid = () => {
        lastSelectionRef.current.forEach((mesh) => {
            applyMaterialModeToMesh(mesh, false);
        });
    };

    const makeSelectedTransparent = () => {
        lastSelectionRef.current.forEach((mesh) => {
            applyMaterialModeToMesh(mesh, true);
        });
    };

    const restoreMaterialState = (mat) => {
        if (!mat) return;
        const stored = mat.userData?.__origMaterialState;
        if (stored) {
            mat.opacity = stored.opacity;
            mat.transparent = stored.transparent;
            mat.depthWrite = stored.depthWrite;
        } else {
            mat.opacity = 1;
            mat.transparent = false;
            mat.depthWrite = true;
        }
        mat.needsUpdate = true;
    };

    const disposeMaterial = (material) => {
        if (Array.isArray(material)) {
            material.forEach((mat) => {
                if (mat && mat.dispose) mat.dispose();
            });
        } else if (material && material.dispose) {
            material.dispose();
        }
    };

    const setGlobalTransparency = (enabled, options = {}) => {
        if (!scene) return;
        const { opacity = 0.15, exceptMeshes = [] } = options;
        const exceptSet = new Set((exceptMeshes || []).filter(Boolean));
        const exceptKey = Array.from(exceptSet)
            .map((mesh) => mesh.uuid)
            .sort()
            .join('|');
        const state = globalTransparencyStateRef.current;
        const opacityChanged = Math.abs((state.opacity ?? 0.15) - opacity) > 1e-4;

        if (
            state.enabled === enabled &&
            state.exceptKey === exceptKey &&
            !opacityChanged
        ) {
            return;
        }

        scene.traverse((obj) => {
            if (!obj.isMesh) return;
            const isExcept = exceptSet.has(obj);

            // While transparency is enabled, exception meshes should stay on their
            // current material (e.g., selection highlight/part style). Keep global-
            // transparency bookkeeping so a later "clearGlobalTransparency(false)"
            // can still restore true original (solid) materials in one step.
            if (enabled && isExcept) {
                if (obj.userData.__globalTransparentApplied) {
                    const original = obj.userData.__globalTransparentOriginalMaterial;
                    const applied = obj.userData.__globalTransparentAppliedMaterial;

                    // If the mesh is still showing the global transparent wrapper,
                    // swap it back to original while it is excepted.
                    if (applied && obj.material === applied && original) {
                        obj.material = original;
                        if (Array.isArray(original)) {
                            original.forEach((mat) => {
                                if (mat) mat.needsUpdate = true;
                            });
                        } else if (original) {
                            original.needsUpdate = true;
                        }
                    }
                }
                return;
            }

            if (enabled && !isExcept) {
                if (!obj.userData.__globalTransparentOriginalMaterial) {
                    obj.userData.__globalTransparentOriginalMaterial = obj.material;
                }
                const original = obj.userData.__globalTransparentOriginalMaterial;
                const clonedMaterial = Array.isArray(original)
                    ? original.map((mat) => (mat && mat.clone ? mat.clone() : mat))
                    : (original && original.clone ? original.clone() : original);

                const applyTransparent = (mat) => {
                    if (!mat) return;
                    if ('opacity' in mat) {
                        mat.transparent = true;
                        mat.opacity = opacity;
                        mat.depthWrite = false;
                    }
                    mat.needsUpdate = true;
                };

                if (Array.isArray(clonedMaterial)) {
                    clonedMaterial.forEach(applyTransparent);
                } else {
                    applyTransparent(clonedMaterial);
                }

                if (obj.userData.__globalTransparentAppliedMaterial) {
                    disposeMaterial(obj.userData.__globalTransparentAppliedMaterial);
                }
                obj.userData.__globalTransparentAppliedMaterial = clonedMaterial;
                obj.material = clonedMaterial;
                obj.userData.__globalTransparentApplied = true;
                return;
            }

            if (obj.userData.__globalTransparentApplied) {
                const original = obj.userData.__globalTransparentOriginalMaterial;
                const applied = obj.userData.__globalTransparentAppliedMaterial;
                if (original && (!applied || obj.material === applied)) {
                    obj.material = original;
                    if (Array.isArray(original)) {
                        original.forEach((mat) => {
                            if (mat) mat.needsUpdate = true;
                        });
                    } else if (original) {
                        original.needsUpdate = true;
                    }
                } else if (obj.material) {
                    const materials = Array.isArray(obj.material)
                        ? obj.material
                        : [obj.material];
                    materials.forEach((mat) => restoreMaterialState(mat));
                }
                if (obj.userData.__globalTransparentAppliedMaterial) {
                    disposeMaterial(obj.userData.__globalTransparentAppliedMaterial);
                }
                delete obj.userData.__globalTransparentOriginalMaterial;
                delete obj.userData.__globalTransparentAppliedMaterial;
                delete obj.userData.__globalTransparentApplied;
            }
        });

        globalTransparencyStateRef.current = {
            enabled,
            opacity,
            exceptKey,
        };
    };

    const showMachineTransparentExceptSelected = (options = {}) => {
        const { opacity = 0.15 } = options || {};
        const selectedMeshes = lastSelectionRef.current || [];
        setGlobalTransparency(true, { opacity, exceptMeshes: selectedMeshes });
    };

    const showMachineTransparentExceptSelectionKeys = (selectionKeys, options = {}) => {
        const { opacity = 0.15 } = options || {};
        const exceptMeshes = collectMeshesForSelectionKeys(selectionKeys);
        setGlobalTransparency(true, { opacity, exceptMeshes });
    };

    const clearGlobalTransparency = () => {
        setGlobalTransparency(false);
    };

    const setIsometricView = () => {
        const defaultPos = defaultCameraPosRef.current;
        const defaultTarget = defaultTargetRef.current;
        if (!defaultPos || !defaultTarget) return;

        camera.position.copy(defaultPos);
        camera.up.copy(getCameraUpVectorForAxis());
        camera.updateProjectionMatrix();

        if (controlsRef.current) {
            controlsRef.current.target.copy(defaultTarget);
            if (controlsRef.current.position0) {
                controlsRef.current.position0.copy(defaultPos);
            }
            if (controlsRef.current.target0) {
                controlsRef.current.target0.copy(defaultTarget);
            }
            if (controlsRef.current.up0) {
                controlsRef.current.up0.copy(camera.up);
            }
            controlsRef.current.update();
        }
    };

    const resetView = () => {
        cameraTransitionRef.current = null;
        resetSceneState();
        setIsometricView();
    };

    // ---------- Tree visibility API ----------
    const setAssemblyNodeVisibility = (
        productRef,
        visible,
        options = {}
    ) => {
        const rootAssembly = assemblyTreeRef.current;
        if (!rootAssembly) return;

        const { skipNotify = false } = options;

        const nodes = findNodeByProductRef(rootAssembly, productRef);
        if (!nodes.length) return;

        nodes.forEach((node) => {
            if (!Array.isArray(node.objects)) return;

            node.objects.forEach((rootObj) => {
                if (!rootObj) return;

                // If making visible, clear tree flag on ancestors so hierarchy can render again
                if (visible) {
                    let current = rootObj;
                    while (current && current !== scene) {
                        current.userData.__hiddenByTree = false;
                        // We also allow tree "show" to override tool hiding
                        current.userData.__hiddenByTool = false;
                        applyVisibilityFromFlags(current);
                        current = current.parent;
                    }
                }

                rootObj.traverse((obj) => {
                    if (!obj.isObject3D) return;
                    if (visible) {
                        obj.userData.__hiddenByTree = false;
                        obj.userData.__hiddenByTool = false;
                    } else {
                        obj.userData.__hiddenByTree = true;
                    }
                    applyVisibilityFromFlags(obj);
                });
            });
        });

        if (!skipNotify) {
            notifyVisibilityChange();
        }
    };

    const setAssemblyNodesVisibility = (productRefs, visible) => {
        if (!Array.isArray(productRefs) || productRefs.length === 0) {
            notifyVisibilityChange();
            return;
        }

        productRefs.forEach((ref) => {
            setAssemblyNodeVisibility(ref, visible, { skipNotify: true });
        });

        notifyVisibilityChange();
    };

    // ---------- UUID-based helpers (public API) ----------
    const getMeshesByUuids = (uuidsInput) => {
        const map = uuidToMeshRef.current;
        const uuids = Array.isArray(uuidsInput) ? uuidsInput : [uuidsInput];
        const result = [];
        uuids.forEach((id) => {
            const mesh = map.get(id);
            if (mesh) result.push(mesh);
        });
        return result;
    };

    const addToHighlight = (uuidsInput) => {
        const meshes = getMeshesByUuids(uuidsInput);
        if (!meshes.length) return;
        highlightMeshesAdd(meshes);
    };

    const removeHighlight = (uuidsInput) => {
        const uuids = Array.isArray(uuidsInput) ? uuidsInput : [uuidsInput];
        const selection = lastSelectionRef.current;
        if (!selection || !selection.length) return;

        const remaining = [];
        selection.forEach((mesh) => {
            if (!mesh) return;
            if (uuids.includes(mesh.uuid)) {
                restoreMeshOriginalMaterial(mesh);
            } else {
                remaining.push(mesh);
            }
        });
        lastSelectionRef.current = remaining;
    };

    const hideUnhideParts = (uuidsInput, visible) => {
        const meshes = getMeshesByUuids(uuidsInput);
        if (!meshes.length) return;

        const rootAssembly = assemblyTreeRef.current;
        const visited = new Set();

        meshes.forEach((mesh) => {
            let roots = [];

            if (rootAssembly) {
                let assemblyNode = findNodeByObject(rootAssembly, mesh.uuid);
                if (!assemblyNode) {
                    let current = mesh.parent;
                    while (current && !assemblyNode) {
                        assemblyNode = findNodeByObject(
                            rootAssembly,
                            current.uuid
                        );
                        current = current.parent;
                    }
                }

                if (
                    assemblyNode &&
                    Array.isArray(assemblyNode.objects) &&
                    assemblyNode.objects.length > 0
                ) {
                    roots = assemblyNode.objects;
                } else {
                    roots = [mesh];
                }
            } else {
                roots = [mesh];
            }

            roots.forEach((root) => {
                root.traverse((obj) => {
                    if (!obj.isObject3D || visited.has(obj)) return;
                    visited.add(obj);
                    obj.userData.__hiddenByTool = !visible;
                    if (visible) {
                        // when explicitly "show", clear tree flag too
                        obj.userData.__hiddenByTree = false;
                    }
                    applyVisibilityFromFlags(obj);
                });
            });
        });

        notifyVisibilityChange();
    };

    const isolatePart = (uuidsInput) => {
        const meshes = getMeshesByUuids(uuidsInput);
        if (!meshes.length) return;

        const rootAssembly = assemblyTreeRef.current;
        const rootsSet = new Set();

        meshes.forEach((mesh) => {
            let assemblyNode = null;
            if (rootAssembly) {
                assemblyNode = findNodeByObject(rootAssembly, mesh.uuid);
                if (!assemblyNode) {
                    let current = mesh.parent;
                    while (current && !assemblyNode) {
                        assemblyNode = findNodeByObject(
                            rootAssembly,
                            current.uuid
                        );
                        current = current.parent;
                    }
                }
            }

            if (
                assemblyNode &&
                Array.isArray(assemblyNode.objects) &&
                assemblyNode.objects.length > 0
            ) {
                assemblyNode.objects.forEach((obj) => rootsSet.add(obj));
            } else {
                rootsSet.add(mesh);
            }
        });

        lastRootsRef.current = Array.from(rootsSet);
        isolateSelected();
    };

    const resetModel = () => {
        resetView();
    };

    // ---------- Selection logic ----------
    const getOccurrenceRootForMesh = (mesh, assemblyNode) => {
        if (
            !assemblyNode ||
            !Array.isArray(assemblyNode.objects) ||
            assemblyNode.objects.length === 0
        ) {
            return mesh;
        }

        const roots = assemblyNode.objects;
        let current = mesh;
        while (current) {
            if (roots.includes(current)) {
                return current; // occurrence root
            }
            current = current.parent;
        }

        return mesh;
    };

    const clearSelection = () => {
        resetSelectionTracking();
    };

    const findAssemblyNodeForMesh = (mesh, rootAssembly) => {
        if (!mesh || !rootAssembly) return null;

        let assemblyNode = findNodeByObject(rootAssembly, mesh.uuid);
        if (assemblyNode) return assemblyNode;

        let current = mesh.parent;
        while (current && !assemblyNode) {
            assemblyNode = findNodeByObject(rootAssembly, current.uuid);
            current = current.parent;
        }
        return assemblyNode;
    };

    const selectMesh = (
        mesh,
        { highlight = true, additive = false, selectionPoint = null } = {}
    ) => {
        if (!mesh) return;

        const rootAssembly = assemblyTreeRef.current;

        // Non-additive: reset previous logical selection
        if (!additive) {
            resetSelectionTracking({ notifyPartDeselected: false });
        }

        // Find assembly node: first try mesh, then parents
        const rawAssemblyNode = findAssemblyNodeForMesh(mesh, rootAssembly);
        const assemblyNode =
            typeof resolveSelectionNode === 'function'
                ? resolveAssemblyNodeFromResolverResult(
                    resolveSelectionNode(rawAssemblyNode),
                    rawAssemblyNode
                )
                : rawAssemblyNode;

        // Determine occurrence root: only the clicked occurrence, not all occurrences
        const occurrenceRoot = getOccurrenceRootForMesh(mesh, assemblyNode);
        const selectionRoots = [occurrenceRoot];

        // Update root set
        if (additive) {
            const existingRootsSet = new Set(lastRootsRef.current || []);
            selectionRoots.forEach((r) => existingRootsSet.add(r));
            lastRootsRef.current = Array.from(existingRootsSet);
        } else {
            lastRootsRef.current = selectionRoots;
        }

        // Highlight all meshes under this occurrence root (depending on mode)
        if (highlight) {
            const meshesToHighlight = new Set();
            selectionRoots.forEach((root) => {
                root.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        meshesToHighlight.add(child);
                    }
                });
            });

            if (additive) {
                highlightMeshesAdd(Array.from(meshesToHighlight));
            } else {
                highlightMeshesReplace(Array.from(meshesToHighlight));
            }
        }

        const materialInfo =
            mesh.material && mesh.material.color
                ? {
                    name: mesh.material.name,
                    color: `#${mesh.material.color.getHexString()}`,
                }
                : mesh.material
                    ? { name: mesh.material.name || '', color: null }
                    : null;
        const normalizedSelectionPoint =
            selectionPoint instanceof THREE.Vector3
                ? selectionPoint.clone()
                : selectionPoint &&
                    Number.isFinite(selectionPoint.x) &&
                    Number.isFinite(selectionPoint.y) &&
                    Number.isFinite(selectionPoint.z)
                    ? new THREE.Vector3(
                        selectionPoint.x,
                        selectionPoint.y,
                        selectionPoint.z
                    )
                    : null;
        lastSelectionPointRef.current = normalizedSelectionPoint;

        const partMeta = {
            name: mesh.name,
            uuid: mesh.uuid,
            type: mesh.type,
            userData: mesh.userData,
            material: materialInfo,
            object3D: mesh,
            assemblyNode,
            selectionPoint: normalizedSelectionPoint,
        };

        // Maintain logical selection meta list (per assembly node/productRef or uuid)
        const key =
            assemblyNode &&
                assemblyNode.productRef !== null &&
                assemblyNode.productRef !== undefined
                ? `p:${assemblyNode.productRef}`
                : `u:${mesh.uuid}`;

        if (additive) {
            if (!selectionKeySetRef.current.has(key)) {
                selectionKeySetRef.current.add(key);
                selectedPartsMetaRef.current = [
                    ...selectedPartsMetaRef.current,
                    partMeta,
                ];
            }
        } else {
            selectionKeySetRef.current = new Set([key]);
            selectedPartsMetaRef.current = [partMeta];
        }

        if (onPartSelected) {
            onPartSelected(partMeta);
        }
        if (onSelectionChange) {
            onSelectionChange([...selectedPartsMetaRef.current]);
        }

        // Reset zoom-to-selected toggle whenever selection changes
        zoomToggleStateRef.current.isZoomed = false;
        zoomToggleStateRef.current.prevPos = null;
        zoomToggleStateRef.current.prevTarget = null;

        // Reset show-parent stepping when selection changes
        showParentStateRef.current = null;

        return partMeta;
    };

    const getPointerSelectionKey = (mesh) => {
        if (!mesh) return null;

        const rootAssembly = assemblyTreeRef.current;
        const rawAssemblyNode = findAssemblyNodeForMesh(mesh, rootAssembly);
        const assemblyNode =
            typeof resolveSelectionNode === 'function'
                ? resolveAssemblyNodeFromResolverResult(
                    resolveSelectionNode(rawAssemblyNode),
                    rawAssemblyNode
                )
                : rawAssemblyNode;

        if (
            assemblyNode &&
            assemblyNode.productRef !== null &&
            assemblyNode.productRef !== undefined
        ) {
            return `p:${String(assemblyNode.productRef).trim()}`;
        }

        const occurrenceRoot = getOccurrenceRootForMesh(mesh, assemblyNode);
        return occurrenceRoot?.uuid ? `u:${occurrenceRoot.uuid}` : `u:${mesh.uuid}`;
    };

    const findRepresentativeMeshForProductRef = (productRef) => {
        const rootAssembly = assemblyTreeRef.current;
        if (!rootAssembly) return null;

        const nodes = findNodeByProductRef(rootAssembly, productRef);
        if (!nodes.length) return null;

        const findFirstMeshInRoot = (rootObj) => {
            if (!rootObj) return null;
            let foundMesh = null;
            rootObj.traverse((child) => {
                if (!foundMesh && child instanceof THREE.Mesh) {
                    foundMesh = child;
                }
            });
            return foundMesh;
        };

        let representativeMesh = null;
        for (const node of nodes) {
            if (!Array.isArray(node.objects) || node.objects.length === 0) {
                continue;
            }

            for (const rootObj of node.objects) {
                representativeMesh = findFirstMeshInRoot(rootObj);
                if (representativeMesh) break;
            }

            if (representativeMesh) break;
        }

        return representativeMesh || null;
    };

    const canSelectAssemblyNode = (productRef) =>
        !!findRepresentativeMeshForProductRef(productRef);

    const selectAssemblyNode = (productRef) => {
        const representativeMesh = findRepresentativeMeshForProductRef(productRef);
        if (!representativeMesh) return false;

        // This will also call onPartSelected and highlight only one occurrence
        selectMesh(representativeMesh, { highlight: true, additive: false });
        return true;
    };

    const selectAssemblySubtreeByProductRefs = (productRefsInput, options = {}) => {
        const rootAssembly = assemblyTreeRef.current;
        if (!rootAssembly) return false;

        const refs = (Array.isArray(productRefsInput) ? productRefsInput : [productRefsInput])
            .map((ref) => String(ref ?? '').trim())
            .filter(Boolean);

        if (!refs.length) return false;

        const {
            primaryProductRef = refs[0],
            focus = true,
            emitSelectionCallbacks = true,
            includeAllOccurrences = true,
            selectionNode = null,
        } = options || {};

        const rootObjectsSet = new Set();
        if (includeAllOccurrences) {
            refs.forEach((ref) => {
                const nodes = findNodeByProductRef(rootAssembly, ref);
                nodes.forEach((node) => {
                    if (!Array.isArray(node.objects)) return;
                    node.objects.forEach((rootObj) => {
                        if (rootObj) rootObjectsSet.add(rootObj);
                    });
                });
            });
        } else {
            const preferredRefs = [primaryProductRef, ...refs].filter(Boolean);
            let singleRoot = null;
            for (const ref of preferredRefs) {
                const nodes = findNodeByProductRef(rootAssembly, ref);
                for (const node of nodes) {
                    if (!Array.isArray(node.objects) || !node.objects.length) continue;
                    singleRoot = node.objects[0];
                    if (singleRoot) break;
                }
                if (singleRoot) break;
            }
            if (singleRoot) {
                rootObjectsSet.add(singleRoot);
            }
        }

        const roots = Array.from(rootObjectsSet);
        if (!roots.length) return false;

        const meshesToHighlight = new Set();
        roots.forEach((rootObj) => {
            rootObj.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    meshesToHighlight.add(child);
                }
            });
        });

        if (!meshesToHighlight.size) return false;

        lastRootsRef.current = roots;
        clearLastSelection();
        highlightMeshesReplace(Array.from(meshesToHighlight));

        const representativeMesh =
            findRepresentativeMeshForProductRef(primaryProductRef) ||
            findRepresentativeMeshForProductRef(refs[0]);

        if (representativeMesh) {
            const rawAssemblyNode =
                selectionNode ||
                findAssemblyNodeForMesh(
                    representativeMesh,
                    rootAssembly
                );
            const assemblyNode =
                typeof resolveSelectionNode === 'function'
                    ? resolveAssemblyNodeFromResolverResult(
                        resolveSelectionNode(rawAssemblyNode),
                        rawAssemblyNode
                    )
                    : rawAssemblyNode;

            const materialInfo =
                representativeMesh.material && representativeMesh.material.color
                    ? {
                        name: representativeMesh.material.name,
                        color: `#${representativeMesh.material.color.getHexString()}`,
                    }
                    : representativeMesh.material
                        ? { name: representativeMesh.material.name || '', color: null }
                        : null;

            const partMeta = {
                name: representativeMesh.name,
                uuid: representativeMesh.uuid,
                type: representativeMesh.type,
                userData: representativeMesh.userData,
                material: materialInfo,
                object3D: representativeMesh,
                assemblyNode,
            };

            const key =
                assemblyNode &&
                    assemblyNode.productRef !== null &&
                    assemblyNode.productRef !== undefined
                    ? `p:${assemblyNode.productRef}`
                    : `u:${representativeMesh.uuid}`;

            selectionKeySetRef.current = new Set([key]);
            selectedPartsMetaRef.current = [partMeta];

            if (emitSelectionCallbacks && onPartSelected) {
                onPartSelected(partMeta);
            }
            if (emitSelectionCallbacks && onSelectionChange) {
                onSelectionChange([...selectedPartsMetaRef.current]);
            }
        }

        zoomToggleStateRef.current.isZoomed = false;
        zoomToggleStateRef.current.prevPos = null;
        zoomToggleStateRef.current.prevTarget = null;
        showParentStateRef.current = null;

        if (focus) {
            focusOnRoots(roots);
        }

        return true;
    };

    // Expand current selection to all occurrences of the selected logical parts
    const expandSelectionToAllOccurrences = () => {
        const metas = selectedPartsMetaRef.current;
        if (!metas || metas.length === 0) return;

        const rootsSet = new Set();
        const meshesToHighlight = new Set();

        metas.forEach((meta) => {
            const node = meta.assemblyNode;
            if (
                node &&
                Array.isArray(node.objects) &&
                node.objects.length > 0
            ) {
                node.objects.forEach((rootObj) => {
                    if (!rootObj) return;
                    rootsSet.add(rootObj);
                    rootObj.traverse((child) => {
                        if (child instanceof THREE.Mesh) {
                            meshesToHighlight.add(child);
                        }
                    });
                });
            } else if (meta.object3D) {
                const rootObj = meta.object3D;
                rootsSet.add(rootObj);
                rootObj.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        meshesToHighlight.add(child);
                    }
                });
            }
        });

        lastRootsRef.current = Array.from(rootsSet);
        clearLastSelection();
        highlightMeshesReplace(Array.from(meshesToHighlight));
    };

    const selectSingleOccurrence = (uuid) => {
        const mesh = uuidToMeshRef.current.get(uuid);
        if (!mesh) return;
        // Standard select: only clicked occurrence
        selectMesh(mesh, { highlight: true, additive: false });
    };

    // ---------- Explosion helpers ----------
    const buildExplodeData = (rootAssembly, center, radius) => {
        const entries = [];
        const visitedRoots = new Set();

        if (rootAssembly && Array.isArray(rootAssembly.children)) {
            rootAssembly.children.forEach((node) => {
                if (!Array.isArray(node.objects) || node.objects.length === 0)
                    return;
                const rootObj = node.objects[0];
                if (!rootObj || visitedRoots.has(rootObj)) return;
                visitedRoots.add(rootObj);

                const box = new THREE.Box3().setFromObject(rootObj);
                if (box.isEmpty()) return;
                const partCenterWorld = new THREE.Vector3();
                box.getCenter(partCenterWorld);
                const parent = rootObj.parent || scene;
                const centerLocal = parent.worldToLocal(center.clone());
                const partCenterLocal = parent.worldToLocal(
                    partCenterWorld.clone()
                );
                const dir = partCenterLocal.sub(centerLocal).normalize();
                if (dir.lengthSq() === 0) return;

                if (!rootObj.userData.__basePosition) {
                    rootObj.userData.__basePosition = rootObj.position.clone();
                }
                const basePosition = rootObj.userData.__basePosition.clone();

                entries.push({
                    root: rootObj,
                    basePosition,
                    direction: dir,
                });
            });
        }

        // Fallback: use scene children if assembly structure doesn't give us groups
        if (!entries.length) {
            const visited = new Set();
            scene.children.forEach((child) => {
                if (!child || visited.has(child)) return;
                visited.add(child);
                const box = new THREE.Box3().setFromObject(child);
                if (box.isEmpty()) return;
                const partCenterWorld = new THREE.Vector3();
                box.getCenter(partCenterWorld);
                const parent = child.parent || scene;
                const centerLocal = parent.worldToLocal(center.clone());
                const partCenterLocal = parent.worldToLocal(
                    partCenterWorld.clone()
                );
                const dir = partCenterLocal.sub(centerLocal).normalize();
                if (dir.lengthSq() === 0) return;
                if (!child.userData.__basePosition) {
                    child.userData.__basePosition = child.position.clone();
                }
                entries.push({
                    root: child,
                    basePosition: child.userData.__basePosition.clone(),
                    direction: dir,
                });
            });
        }

        explodeDataRef.current = {
            entries,
            center: center.clone(),
            radius: radius || 1,
        };
    };

    const resetExplodePositions = () => {
        const data = explodeDataRef.current;
        if (!data || !Array.isArray(data.entries)) return;
        data.entries.forEach((entry) => {
            if (!entry?.root) return;
            const base = entry.root.userData.__basePosition || entry.basePosition;
            if (!base) return;
            entry.root.position.copy(base);
        });
    };

    // ---------- Zoom helpers ----------
    const zoomCameraByFactor = (factor) => {
        const controls = controlsRef.current;
        if (!controls) return;

        const target =
            controls.target ||
            defaultTargetRef.current ||
            new THREE.Vector3(0, 0, 0);

        const dir = new THREE.Vector3().subVectors(camera.position, target);
        const currentDist = dir.length();
        if (!currentDist) return;

        const radius = modelRadiusRef.current || 1;
        const minDist = radius * 0.1;
        const maxDist = radius * 20;

        let newDist = currentDist * factor;
        newDist = THREE.MathUtils.clamp(newDist, minDist, maxDist);

        dir.setLength(newDist);
        const newPos = new THREE.Vector3().addVectors(target, dir);

        camera.position.copy(newPos);
        camera.updateProjectionMatrix();
        controls.update();
    };

    const zoomCameraTowardPoint = (point, factor) => {
        const controls = controlsRef.current;
        if (!controls || !point) {
            zoomCameraByFactor(factor);
            return;
        }

        const currentTarget = controls.target.clone();
        const currentDistance = camera.position.distanceTo(currentTarget);
        if (!currentDistance) return;

        const radius = modelRadiusRef.current || 1;
        const minDist = radius * 0.02;
        const maxDist = radius * 20;
        const nextDistance = THREE.MathUtils.clamp(
            currentDistance * factor,
            minDist,
            maxDist
        );
        const ratio = nextDistance / currentDistance;

        const nextPosition = point
            .clone()
            .add(camera.position.clone().sub(point).multiplyScalar(ratio));
        const nextTarget = point
            .clone()
            .add(currentTarget.sub(point).multiplyScalar(ratio));

        camera.position.copy(nextPosition);
        controls.target.copy(nextTarget);
        camera.updateProjectionMatrix();
        controls.update();
    };

    const getZoomPointFromWheelEvent = (event) => {
        if (!gl?.domElement || !raycaster || !size?.width || !size?.height) {
            return null;
        }

        const rect = gl.domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );

        raycaster.setFromCamera(ndc, camera);
        const meshes = Array.from(allMeshesRef.current || []).filter(
            (mesh) => mesh?.visible
        );
        const intersections = raycaster.intersectObjects(meshes, false);
        return intersections[0]?.point || controlsRef.current?.target?.clone() || null;
    };

    const zoomInStep = () => zoomCameraByFactor(0.8); // closer
    const zoomOutStep = () => zoomCameraByFactor(1.25); // farther

    const zoomTowardPoint = (point, factor = 0.85) => {
        const controls = controlsRef.current;
        if (!controls || !point) return;

        controls.target.copy(point);
        controls.update();
        zoomCameraByFactor(factor);
    };

    const zoomToSelectionToggle = () => {
        const controls = controlsRef.current;
        if (!controls) return;

        const state = zoomToggleStateRef.current;

        // If currently zoomed, restore previous camera state
        if (state.isZoomed && state.prevPos && state.prevTarget) {
            cameraTransitionRef.current = null;
            camera.position.copy(state.prevPos);
            camera.updateProjectionMatrix();
            controls.target.copy(state.prevTarget);
            controls.update();

            state.isZoomed = false;
            return;
        }

        // Otherwise, zoom into current selection
        const roots =
            lastRootsRef.current && lastRootsRef.current.length
                ? lastRootsRef.current
                : [];

        if (!roots.length) {
            return; // nothing selected
        }

        // Save current camera state
        state.prevPos = camera.position.clone();
        state.prevTarget = controls.target.clone();

        // Focus on selected roots
        focusOnRoots(roots);

        state.isZoomed = true;
    };

    // ---------- Select Visible (frustum-based) ----------
    const selectVisibleParts = () => {
        const rootAssembly = assemblyTreeRef.current;
        if (!rootAssembly) return [];

        const controls = controlsRef.current;
        if (!controls) return [];

        // Build camera frustum
        camera.updateMatrixWorld();
        const projScreenMatrix = new THREE.Matrix4().multiplyMatrices(
            camera.projectionMatrix,
            camera.matrixWorldInverse
        );
        const frustum = new THREE.Frustum();
        frustum.setFromProjectionMatrix(projScreenMatrix);

        const nodesToSelect = [];

        const visit = (node) => {
            if (!node) return;

            if (Array.isArray(node.objects) && node.objects.length > 0) {
                let inView = false;

            for (const rootObj of node.objects) {
                if (!rootObj) continue;

                let anyVisible = false;
                rootObj.traverse((obj) => {
                    if (obj.isMesh && obj.visible) {
                        anyVisible = true;
                    }
                });
                if (!anyVisible) continue;

                const box = new THREE.Box3().setFromObject(rootObj);
                if (!box.isEmpty() && frustum.intersectsBox(box)) {
                    inView = true;
                    break;
                }
            }

            if (inView) nodesToSelect.push(node);
        }

            if (Array.isArray(node.children)) {
                node.children.forEach(visit);
            }
        };

        visit(rootAssembly);

        // If nothing visible → clear selection
        if (!nodesToSelect.length) {
            resetSelectionTracking({ notifyPartDeselected: true });
            return [];
        }

        // CLEAR previous selection and notify deselect
        resetSelectionTracking({ notifyPartDeselected: true });

        const visibleMeshes = new Set();

        // Build selection list (unique meta) + collect all visible meshes
        nodesToSelect.forEach((node, idx) => {
            if (!Array.isArray(node.objects) || !node.objects.length) return;

            let repMesh = null;
            node.objects.forEach((rootObj) => {
                if (!rootObj) return;
                rootObj.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        if (!repMesh) repMesh = child;
                        if (child.visible) visibleMeshes.add(child);
                    }
                });
            });

            if (!repMesh) return;

            selectMesh(repMesh, {
                highlight: false,
                additive: idx !== 0,
            });
        });

        // Deduplicate by assemblyNode.productRef or uuid
        const unique = new Map();
        selectedPartsMetaRef.current.forEach((m) => {
            const key =
                m.assemblyNode?.productRef != null
                    ? `p:${m.assemblyNode.productRef}`
                    : `u:${m.uuid}`;
            if (!unique.has(key)) unique.set(key, m);
        });

        const uniqueList = Array.from(unique.values());

        if (visibleMeshes.size) {
            highlightMeshesReplace(Array.from(visibleMeshes));
        }

        // Notify parent with unique list
        if (onSelectionChange) {
            onSelectionChange(uniqueList);
        }

        return uniqueList;

    };


    // ---------- Show Parent stepping ----------
    const stepShowParentForSelection = () => {
        const rootAssembly = assemblyTreeRef.current;
        if (!rootAssembly) return;
        const parentMap = assemblyParentMapRef.current;
        if (!parentMap || !parentMap.size) return;

        const metas = selectedPartsMetaRef.current;
        if (!metas || !metas.length) return;

        // Gather base assembly nodes for the current selection
        const baseNodes = metas
            .map((m) => m.assemblyNode)
            .filter((n) => !!n);

        if (!baseNodes.length) return;

        // If state is empty, initialize chains and start at step = 1
        if (!showParentStateRef.current) {
            const chains = baseNodes.map((node) => {
                const chain = [];
                let current = node;
                while (current) {
                    chain.push(current);
                    current = parentMap.get(current) || null;
                }
                return chain;
            });

            showParentStateRef.current = {
                baseNodes,
                chains,
                step: 1, // 0 would be just the part; 1 = part + nearest parent
            };
        } else {
            const state = showParentStateRef.current;
            const chains = state.chains || [];
            if (!chains.length) {
                showParentStateRef.current = null;
                return;
            }

            const maxDepthMinusOne = Math.max(
                ...chains.map((c) => c.length - 1)
            );
            if (state.step < maxDepthMinusOne) {
                state.step += 1;
            } else {
                // Already at the top-most parents; nothing more to add
            }
        }

        const { chains, step } = showParentStateRef.current;
        const allowedNodesSet = new Set();

        chains.forEach((chain) => {
            const maxIndex = Math.min(step, chain.length - 1);
            for (let i = 0; i <= maxIndex; i += 1) {
                allowedNodesSet.add(chain[i]);
            }
        });

        const allowedRootsSet = new Set();
        allowedNodesSet.forEach((node) => {
            collectNodeRootObjects(node, allowedRootsSet);
        });

        if (!allowedRootsSet.size && lastRootsRef.current.length) {
            lastRootsRef.current.forEach((root) => {
                if (root) {
                    allowedRootsSet.add(root);
                }
            });
        }

        const allowedAncestorSet = collectAncestorObjects(
            Array.from(allowedRootsSet),
            new Set()
        );

        // Hide everything except nodes under allowedRootsSet
        scene.traverse((obj) => {
            if (!obj.isObject3D || obj === scene) return;
            if (
                isUnderAnyRoot(obj, allowedRootsSet) ||
                allowedAncestorSet.has(obj)
            ) {
                obj.userData.__hiddenByView = false;
                obj.userData.__hiddenByTree = false;
            } else {
                obj.userData.__hiddenByView = true;
            }
            applyVisibilityFromFlags(obj);
        });

        notifyVisibilityChange();

        // Focus camera on the currently allowed nodes (selected part + its parents)
        focusOnRoots(Array.from(allowedRootsSet));
    };

    // Expose methods to parent (ModelViewer)
    useImperativeHandle(ref, () => ({
        resetView,
        setIsometricView,
        hideSelected,
        isolateSelected,
        makeSelectedSolid,
        makeSelectedTransparent,
        setSelectionKeysSolid,
        setSelectionKeysTransparent,
        showMachineTransparentExceptSelected,
        showMachineTransparentExceptSelectionKeys,
        clearGlobalTransparency,
        unhideLastHidden: unhideLastHiddenGroup,
        setAssemblyNodeVisibility,
        setAssemblyNodesVisibility,
        canSelectAssemblyNode,
        selectAssemblyNode,
        selectAssemblySubtreeByProductRefs,
        clearSelection,

        // Helper APIs by UUID
        addToHighlight,
        removeHighlight,
        isolatePart,
        hideUnhideParts,
        resetModel,

        showOnlyProductRefs,

        // Occurrence helpers
        selectAllOccurrences: expandSelectionToAllOccurrences,
        selectSingleOccurrence,

        // Zoom helpers
        zoomInStep,
        zoomOutStep,
        zoomToSelectionToggle,

        // New helpers for your new UI
        selectVisibleParts,
        stepShowParentForSelection,
    }));

    // ---------- Initial setup: fit camera, build mapping, load JSON ----------
    useEffect(() => {
        if (!scene) return;

        camera.up.copy(getCameraUpVectorForAxis());
        applyModelUpAxis(scene, modelAxis, explicitOrientation);

        // Build set of all meshes + uuid map
        const allMeshes = new Set();
        const uuidMap = new Map();

        // scene.traverse((obj) => {
        //     if (obj instanceof THREE.Mesh) {
        //         allMeshes.add(obj);
        //         uuidMap.set(obj.uuid, obj);
        //     }
        // });

        scene.traverse((obj) => {
            if (obj instanceof THREE.Mesh) {
                allMeshes.add(obj);
                uuidMap.set(obj.uuid, obj);

                // --- Make parts pickable from any angle (front & back) ---
                const mat = obj.material;
                if (!mat) return;

                if (Array.isArray(mat)) {
                    mat.forEach((m) => {
                        if (!m) return;
                        m.side = THREE.DoubleSide;
                    });
                } else {
                    mat.side = THREE.DoubleSide;
                }
            }
        });

        allMeshesRef.current = allMeshes;
        uuidToMeshRef.current = uuidMap;

        // Fit & set isometric camera
        const box = new THREE.Box3().setFromObject(scene);
        if (!box.isEmpty()) {
            const center = new THREE.Vector3();
            const size = new THREE.Vector3();
            box.getCenter(center);
            box.getSize(size);

            const maxDim = Math.max(size.x, size.y, size.z);
            const fov = (camera.fov * Math.PI) / 180;
            let dist = Math.abs(maxDim / 2 / Math.tan(fov / 2));
            dist *= 1.5; // padding

            const { dir, distanceMultiplier } = getPreferredIsometricDirection(
                scene.quaternion.clone(),
                defaultCameraCorner
            );
            const isoPos = center.clone().add(dir.multiplyScalar(dist * (distanceMultiplier || 1)));

            camera.position.copy(isoPos);
            camera.near = dist / 100;
            camera.far = dist * 100;
            camera.updateProjectionMatrix();

            if (controlsRef.current) {
                controlsRef.current.target.copy(center);
                if (controlsRef.current.position0) {
                    controlsRef.current.position0.copy(isoPos);
                }
                if (controlsRef.current.target0) {
                    controlsRef.current.target0.copy(center);
                }
                if (controlsRef.current.up0) {
                    controlsRef.current.up0.copy(camera.up);
                }
                controlsRef.current.update();
            }

            // Store defaults for reset / re-iso
            defaultCameraPosRef.current = isoPos.clone();
            defaultTargetRef.current = center.clone();
            modelRadiusRef.current = maxDim * 0.5;
        }

        // ---- Build name -> Object3D[] map from GLB ----
        const nameToObjects = new Map();
        scene.traverse((obj) => {
            const key = (obj.userData && obj.userData.name) || obj.name;
            if (!key) return;
            if (!nameToObjects.has(key)) {
                nameToObjects.set(key, []);
            }
            nameToObjects.get(key).push(obj);
        });

        // ---- Load assembly JSON and attach objects ----
        if (assemblyPath) {
            fetch(assemblyPath, { headers: authHeader() })
                .then((r) => r.json())
                .then((jsonTree) => {
                    attachObjectsToJson(jsonTree, nameToObjects);
                    assemblyTreeRef.current = jsonTree;

                    // Build parent map for assembly nodes
                    const parentMap = buildAssemblyParentMap(jsonTree);
                    assemblyParentMapRef.current = parentMap;

                    if (onAssemblyMapped) {
                        onAssemblyMapped(jsonTree);
                    }

                    // Initial visibility report (all visible)
                    notifyVisibilityChange();

                    // Build explosion data
                    const center =
                        defaultTargetRef.current ||
                        new THREE.Vector3(0, 0, 0);
                    buildExplodeData(
                        jsonTree,
                        center,
                        modelRadiusRef.current || 1
                    );

                    if (typeof onModelLoaded === 'function') {
                        onModelLoaded();
                    }
                })
                .catch((err) => {
                    console.error('Failed to load assembly JSON:', err);
                    // Even if JSON fails, still let parent know GLB is ready
                    if (typeof onModelLoaded === 'function') {
                        onModelLoaded();
                    }
                });
        } else {
            notifyVisibilityChange();
            const center =
                defaultTargetRef.current || new THREE.Vector3(0, 0, 0);
            buildExplodeData(null, center, modelRadiusRef.current || 1);

            if (typeof onModelLoaded === 'function') {
                onModelLoaded();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scene, camera, assemblyPath, onAssemblyMapped, modelAxis, explicitOrientation, defaultCameraCorner]);

    // ---------- Explosion effect ----------
    useEffect(() => {
        const data = explodeDataRef.current;
        if (!data || !data.entries || !data.entries.length) return;

        const factor = explodeFactor ?? 0;
        const radius = data.radius || 1;

        data.entries.forEach((entry) => {
            const { root, basePosition, direction } = entry;
            if (!root || !basePosition || !direction) return;
            const offset = direction
                .clone()
                .multiplyScalar(factor * radius);
            root.position.copy(basePosition.clone().add(offset));
        });
    }, [explodeFactor]);

    useEffect(() => {
        return () => {
            resetExplodePositions();
        };
    }, [glbPath]);

    const syncMouseButtons = (shiftOverride = shiftPanActiveRef.current) => {
        const controls = controlsRef.current;
        if (!controls || !controls.mouseButtons) return;

        if (navMode === 'pan' || shiftOverride) {
            controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
            controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
        } else {
            controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE;
            controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
        }
    };

    // ---------- Update controls for navMode ----------
    useEffect(() => {
        syncMouseButtons();
    }, [navMode]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key !== 'Shift') return;
            shiftPanActiveRef.current = true;
            syncMouseButtons();
        };

        const handleKeyUp = (event) => {
            if (event.key !== 'Shift') return;
            shiftPanActiveRef.current = false;
            syncMouseButtons();
        };

        const resetShiftPan = () => {
            shiftPanActiveRef.current = false;
            syncMouseButtons(false);
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                resetShiftPan();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', resetShiftPan);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', resetShiftPan);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [navMode]);

    useEffect(() => {
        const canvas = gl?.domElement;
        if (!canvas) return undefined;

        const handleWheel = (event) => {
            if (!controlsRef.current) return;

            event.preventDefault();
            event.stopPropagation();

            const delta = THREE.MathUtils.clamp(event.deltaY, -120, 120);
            const factor = Math.pow(0.8, -delta / 120);
            const zoomPoint = getZoomPointFromWheelEvent(event);
            zoomCameraTowardPoint(zoomPoint, factor);
        };

        canvas.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            canvas.removeEventListener('wheel', handleWheel);
        };
    }, [camera, gl, raycaster, size.width, size.height]);

    // Put this above the pointer handlers in ModelScene.jsx
    const getMeshFromPointerEvent = (event) => {
        let obj = event.object;

        // 1) If it's already a Mesh, use it directly
        if (obj instanceof THREE.Mesh) return obj;

        // 2) Walk up the parent chain to find a Mesh
        let current = obj.parent;
        while (current) {
            if (current instanceof THREE.Mesh) return current;
            current = current.parent;
        }

        // 3) Try other intersections (R3F gives you all hits)
        if (Array.isArray(event.intersections)) {
            const meshHit = event.intersections.find(
                (hit) => hit.object instanceof THREE.Mesh
            );
            if (meshHit) return meshHit.object;
        }

        // No mesh found at all
        return null;
    };


    // // ---------- Pointer handlers ----------
    // const handlePointerDown = (event) => {
    //     event.stopPropagation();
    //     const mesh = event.object;
    //     
    //     if (!(mesh instanceof THREE.Mesh)) return;

    //     const additive =
    //         event.ctrlKey ||
    //         event.metaKey ||
    //         (event.sourceEvent &&
    //             (event.sourceEvent.ctrlKey ||
    //                 event.sourceEvent.metaKey));

    //     selectMesh(mesh, { highlight: true, additive });
    // };

    // // Double-click to hide the selected part (FILO push)
    // const handleDoubleClick = (event) => {
    //     event.stopPropagation();
    //     const mesh = event.object;
    //     if (!(mesh instanceof THREE.Mesh)) return;

    //     // For double-click we don't need heavy highlighting again
    //     selectMesh(mesh, { highlight: false, additive: false });
    //     hideSelected();
    // };

    const handlePointerDown = (event) => {
        event.stopPropagation();

        const shiftActive =
            !!event.shiftKey || !!event.sourceEvent?.shiftKey || !!event.nativeEvent?.shiftKey;
        shiftPanActiveRef.current = shiftActive;
        syncMouseButtons(shiftActive);

        const mesh = getMeshFromPointerEvent(event);
        if (!mesh) return;

        const nowTs = Date.now();
        const selectionKey = getPointerSelectionKey(mesh);
        const isSameSelection =
            lastPointerSelectRef.current.selectionKey === selectionKey;
        const isDoubleSelect =
            isSameSelection && (nowTs - lastPointerSelectRef.current.ts) <= 320;
        lastPointerSelectRef.current = { selectionKey, ts: nowTs };

        // Reliable double-click hide path (works even when native onDoubleClick is flaky
        // due to camera controls/pointer interactions).
        if (isDoubleSelect) {
            suppressNativeDoubleClickRef.current = true;
            const partMeta = selectMesh(mesh, {
                highlight: false,
                additive: false,
                selectionPoint: event.point || null,
            });
            if (typeof onPartDoubleClick === 'function') {
                onPartDoubleClick(partMeta);
            }
            return;
        }

        const additive =
            event.ctrlKey ||
            event.metaKey ||
            (event.sourceEvent &&
                (event.sourceEvent.ctrlKey ||
                    event.sourceEvent.metaKey));

        selectMesh(mesh, {
            highlight: true,
            additive,
            selectionPoint: event.point || null,
        });
    };

    const handleDoubleClick = (event) => {
        event.stopPropagation();

        if (suppressNativeDoubleClickRef.current) {
            suppressNativeDoubleClickRef.current = false;
            return;
        }

        const mesh = getMeshFromPointerEvent(event);
        if (!mesh) return;

        const partMeta = selectMesh(mesh, {
            highlight: false,
            additive: false,
            selectionPoint: event.point || null,
        });
        if (typeof onPartDoubleClick === 'function') {
            onPartDoubleClick(partMeta);
        }
    };


    const handlePointerOver = (event) => {
        event.stopPropagation();
        document.body.style.cursor = 'pointer';
    };

    const handlePointerOut = (event) => {
        event.stopPropagation();
        document.body.style.cursor = 'default';
        if (!event.shiftKey && !event.sourceEvent?.shiftKey && !event.nativeEvent?.shiftKey) {
            shiftPanActiveRef.current = false;
            syncMouseButtons(false);
        }
    };

    // ---------- Lighting config ----------
    const lightType = lightSettings?.type || 'studio'; // 'studio' | 'hemisphere' | 'directional'
    const lightIntensity = lightSettings?.intensity ?? 1.0;

    return (
        <>
            {/* Base ambient */}
            <ambientLight intensity={0.3 * lightIntensity} />

            {/* 8-directional studio rig */}
            {lightType === 'studio' && (
                <>
                    {/* 4 diagonals */}
                    <directionalLight
                        position={[5, 5, 5]}
                        intensity={0.8 * lightIntensity}
                    />
                    <directionalLight
                        position={[-5, 5, 5]}
                        intensity={0.8 * lightIntensity}
                    />
                    <directionalLight
                        position={[5, 5, -5]}
                        intensity={0.8 * lightIntensity}
                    />
                    <directionalLight
                        position={[-5, 5, -5]}
                        intensity={0.8 * lightIntensity}
                    />

                    {/* 4 cardinal-ish directions */}
                    <directionalLight
                        position={[10, 0, 0]}
                        intensity={0.6 * lightIntensity}
                    />
                    <directionalLight
                        position={[-10, 0, 0]}
                        intensity={0.6 * lightIntensity}
                    />
                    <directionalLight
                        position={[0, 0, 10]}
                        intensity={0.6 * lightIntensity}
                    />
                    <directionalLight
                        position={[0, 0, -10]}
                        intensity={0.6 * lightIntensity}
                    />
                    <directionalLight
                        position={[0, -10, 0]} // below the model
                        intensity={0.5 * lightIntensity}
                    />
                    {/* Optional extra fill */}
                    <directionalLight
                        position={[5, -5, 5]}
                        intensity={0.4 * lightIntensity}
                    />
                    <directionalLight
                        position={[-5, -5, -5]}
                        intensity={0.4 * lightIntensity}
                    />
                </>
            )}

            {lightType === 'hemisphere' && (
                <hemisphereLight
                    skyColor={0xffffff}
                    groundColor={0x444444}
                    intensity={1.2 * lightIntensity}
                    position={[0, 1, 0]}
                />
            )}

            {lightType === 'directional' && (
                <directionalLight
                    position={[10, 10, 10]}
                    intensity={1.2 * lightIntensity}
                />
            )}

            {/* Trackball controls keep rotation continuous and avoid orbit pole locking. */}
            {/* <TrackballControls
                ref={controlsRef}
                makeDefault
                noZoom
            /> */}
            <TrackballControls
                ref={controlsRef}
                makeDefault
                noZoom
                staticMoving
                rotateSpeed={5}
                panSpeed={1.4}
                dynamicDampingFactor={0.08}
            />

            {/* GLB model */}
            <primitive
                object={scene}
                onPointerDown={handlePointerDown}
                onDoubleClick={handleDoubleClick}
                onPointerOver={handlePointerOver}
                onPointerOut={handlePointerOut}
            />
        </>
    );
});

// useGLTF.preload('/models/model.glb');

export default ModelScene;
